import { INSIGHT_TYPES } from './types'

/* The policy field contract: every row scoping may be asked, plus the rows that
   are deliberately dormant, in one numbered list. Pure — no DO, no network —
   so the admin app and the Durable Object read the same definitions through
   `@worker/policy-builder/client`.

   It absorbs the admin-side handoff checklist: a row that scoping answers
   through a control carries that control here, and its `id` is the key its
   answer is stored under. Ids for rows that already had an answer key keep it;
   every other id is the row's own label in kebab case. */

export type ContractStart = 'DERIVED' | 'DEFAULT' | 'ASK' | 'ENTERED'

export type ContractKind =
  | 'asked'
  | 'open'
  | 'gated'
  | 'notasked'
  | 'inherited'
  | 'system'
  | 'retired'
  | 'dropped'

export type ContractGroup =
  | 'Classification'
  | 'Intent and narrative'
  | 'Code data'
  | 'Conditional decision set'
  | 'Sources and evidence'
  | 'Timeline'
  | 'Commitment'
  | 'System and PM-ops'
  | 'Notes for design'

export type ContractStatus =
  | 'Accepted'
  | 'Needs answer'
  | 'Confirm or N/A'
  | 'Optional'

export type HandoffControl =
  | 'text'
  | 'prose'
  | 'chips'
  | 'chip-list'
  | 'codes'
  | 'select-other'
  | 'date'

/** A single answer. Kept flat: the shape depends on the control that owns it. */
export type AnswerValue = string | string[] | { choice: string; other?: string }

export interface ContractField {
  /** Stable key the answer is stored under. */
  id: string
  num: string
  label: string
  group: ContractGroup
  start: ContractStart
  required: string
  kind: ContractKind
  ref: string
  help?: string
  gate?: string
  note?: string
  nullable?: boolean
  /** `false` when the answer stands on its own and needs no citation or
   *  explanation (e.g. the policy's name); its Why is not shown. */
  basis?: false
  control?: HandoffControl
  prompt?: string
  options?: string[]
  choices?: { label: string; value: string }[]
  /** Code-list summary template; `{lead}`, `{rest}` and `{all}` substitute. */
  summary?: string
  leadCount?: number
  placeholder?: string
  /** Present only when the row is asked conditionally; absent means always. */
  askedWhen?: (answers: Record<string, AnswerValue>) => boolean
}

export const CONTRACT_GROUP_ORDER: ContractGroup[] = [
  'Classification',
  'Intent and narrative',
  'Code data',
  'Conditional decision set',
  'Sources and evidence',
  'Timeline',
  'Commitment',
  'System and PM-ops',
  /* Last, so scoping writes its note once every answer above is in (S-N11). */
  'Notes for design',
]

export const DX_LOGIC = 'Diagnosis Valid Code Use and Medical Necessity'
const UNITS_LOGIC = 'Units/Frequency'

export const LOGIC_OPTIONS = [
  'Age',
  'Attached Documentation',
  'Covered Service',
  DX_LOGIC,
  'Duplicate',
  'Global Days',
  'Manual Review',
  'Modifier',
  'Non-Covered Service',
  'Place of Service',
  'Pricing',
  'Prior Authorization',
  'Procedure Code Combinations and Relationship',
  UNITS_LOGIC,
]

export const asList = (value: AnswerValue | undefined): string[] =>
  Array.isArray(value) ? value : []

/**
 * The select-other branch of an answer. Given its field, a plain string —
 * stored before the field had fixed choices — reads as the choice it names,
 * by value or label, and as Something else otherwise.
 */
export const choiceAnswer = (
  value: AnswerValue | undefined,
  field?: ContractField,
): { choice: string; other?: string } | undefined => {
  if (typeof value === 'string') {
    const text = value.trim()
    if (!text || !field?.choices) return undefined
    const key = text.toLowerCase()
    const match = field.choices.find(
      (c) =>
        c.value !== 'other' &&
        (c.value.toLowerCase() === key || c.label.toLowerCase() === key),
    )
    return match ? { choice: match.value } : { choice: 'other', other: text }
  }
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : undefined
}

export const logicIncludes =
  (logic: string) =>
  (answers: Record<string, AnswerValue>): boolean =>
    asList(answers['logic-types']).includes(logic)

const unitsGate = 'Gated by logic type · Units/Frequency'

export const CONTRACT_FIELDS: ContractField[] = [
  {
    id: 'policy-name',
    num: '1',
    label: 'Policy name',
    group: 'Classification',
    start: 'DERIVED',
    required: 'Y',
    kind: 'asked',
    ref: 'contract 1.0',
    nullable: false,
    basis: false,
    control: 'text',
  },
  {
    id: 'topic',
    num: '3',
    label: 'Topic',
    group: 'Classification',
    start: 'DERIVED',
    required: 'Y',
    kind: 'asked',
    ref: 'contract 3.0 · 49-value list',
    nullable: false,
  },
  {
    id: 'lines-of-business',
    num: '4',
    label: 'Line of business',
    group: 'Classification',
    start: 'DERIVED',
    required: 'Y',
    kind: 'asked',
    ref: 'contract 4.0',
    nullable: false,
    control: 'chips',
    options: ['Medicare', 'Medicaid', 'Commercial'],
  },
  {
    id: 'engine-type',
    num: '5',
    label: 'Engine type',
    group: 'Classification',
    start: 'ASK',
    required: '—',
    kind: 'retired',
    ref: 'contract 5',
    note: 'Retiring — engine selection is an implementation concern. Content should never see an SPE / DDE difference.',
  },
  {
    id: 'logic-types',
    num: '6',
    label: 'Logic type(s)',
    group: 'Classification',
    start: 'DERIVED',
    required: 'Y',
    kind: 'asked',
    ref: 'contract 6.0 · up to 4 · gates section D',
    nullable: false,
    control: 'chip-list',
    options: LOGIC_OPTIONS,
    prompt: 'Determines which questions appear below.',
  },
  {
    id: 'edit-type',
    num: '7',
    label: 'Edit type',
    group: 'Classification',
    start: 'ASK',
    required: '—',
    kind: 'retired',
    ref: 'contract 7',
    note: 'Retired 2026-09-13 as a duplicate of 6.0 logic type. Carrying both invited the same question answered twice in different words.',
  },
  {
    id: 'claim-types',
    num: '8',
    label: 'Claim type(s)',
    group: 'Classification',
    start: 'DERIVED',
    required: 'Y',
    kind: 'asked',
    ref: 'contract 8.0 · up to 5',
    nullable: false,
    control: 'chips',
    options: [
      'Professional',
      'ASC Billed on Professional',
      'ASC Billed on Institutional',
      'Institutional Outpatient',
      'Institutional Inpatient',
    ],
  },
  {
    id: 'intent',
    num: '9',
    label: 'Policy intent',
    group: 'Intent and narrative',
    start: 'DERIVED',
    required: 'Y',
    kind: 'asked',
    ref: 'contract 9.0 · no nesting, no order of operations',
    nullable: false,
    control: 'prose',
  },
  {
    id: 'description',
    num: '10',
    label: 'Client-facing policy description',
    group: 'Intent and narrative',
    start: 'DERIVED',
    required: 'Y',
    kind: 'asked',
    ref: 'contract 10.0 · derived from 9.0',
    nullable: false,
    control: 'prose',
    prompt:
      'Drafted from your answers. Edit anything that does not sound like us.',
  },
  {
    id: 'scoping-notes',
    num: '10.1',
    label: 'Scoping notes',
    group: 'Notes for design',
    start: 'ENTERED',
    required: 'N',
    kind: 'asked',
    ref: 'contract 10.1 · optional',
    nullable: false,
    basis: false,
  },
  {
    id: 'procedure-codes',
    num: '11',
    label: 'Procedure codes',
    group: 'Code data',
    start: 'DERIVED',
    required: 'Y',
    kind: 'asked',
    ref: 'contract 11.0 · explicit, enumerated, no ranges',
    nullable: false,
    control: 'codes',
    leadCount: 1,
    summary: '{lead} under evaluation · {rest} base codes · {all} total',
  },
  {
    /* Not one of the 39 contract rows: carried over from the handoff checklist
       because the test engine reads it. */
    id: 'diagnosis-codes',
    num: '11.1',
    label: 'Approved diagnosis codes',
    group: 'Code data',
    start: 'ASK',
    required: 'Y*',
    kind: 'asked',
    ref: 'checklist · diagnosis-codes',
    gate: `Gated by logic type · ${DX_LOGIC}`,
    nullable: false,
    control: 'codes',
    summary: '{all} approved diagnoses',
    askedWhen: logicIncludes(DX_LOGIC),
  },
  {
    /* Not one of the 39 contract rows: carried over from the handoff checklist
       because the test engine reads it. */
    id: 'code-combination',
    num: '11.2',
    label: 'Code combination relationship',
    group: 'Code data',
    start: 'ASK',
    required: 'As needed',
    kind: 'asked',
    ref: 'checklist · code-combination',
    nullable: false,
    control: 'select-other',
    choices: [
      {
        label:
          'The code under evaluation requires a companion code from the base list',
        value: 'companion',
      },
      { label: 'The codes are mutually exclusive', value: 'mutual' },
      { label: 'No relationship — each code stands alone', value: 'none' },
      { label: 'Something else — describe it', value: 'other' },
    ],
  },
  {
    id: 'modifiers',
    num: '12',
    label: 'Modifiers',
    group: 'Code data',
    start: 'ASK',
    required: '—',
    kind: 'notasked',
    ref: 'contract 12',
    note: 'No modifier condition stated in any source for this policy.',
  },
  {
    id: 'provider-type-differentiation',
    num: '13',
    label: 'Provider-type differentiation',
    group: 'Code data',
    start: 'ASK',
    required: 'If applies',
    kind: 'asked',
    ref: 'contract 13.0 · taxonomy, included or excluded',
    help: 'Which provider types the policy applies to, by taxonomy code, and whether any criteria, limits or outcomes differ by type. Not the same question as 18.0 provider matching.',
    nullable: false,
  },
  {
    id: 'units-or-frequency',
    num: '14',
    label: 'Units or frequency',
    group: 'Conditional decision set',
    start: 'ASK',
    required: 'Y*',
    kind: 'asked',
    ref: 'contract 14.0',
    gate: unitsGate,
    nullable: false,
    askedWhen: logicIncludes(UNITS_LOGIC),
  },
  {
    id: 'ocl-history-choice',
    num: '15',
    label: 'OCL / history choice',
    group: 'Conditional decision set',
    start: 'ASK',
    required: 'Y*',
    kind: 'asked',
    ref: 'contract 15.0 · OCL-S / OCL-D / Both',
    gate: unitsGate,
    nullable: false,
    askedWhen: logicIncludes(UNITS_LOGIC),
    control: 'select-other',
    /* Values are Nucleus's own `history_scope` keys, so its proposals land as-is. */
    choices: [
      { label: 'OCL-S — other claim line, same claim', value: 'ocl_s' },
      { label: 'OCL-D — other claim line, different claim', value: 'ocl_d' },
      { label: 'Both', value: 'both' },
    ],
  },
  {
    id: 'history-claim-type-matching',
    num: '15.1',
    label: 'History claim-type matching',
    group: 'Conditional decision set',
    start: 'ASK',
    required: 'Y*',
    kind: 'asked',
    ref: 'contract 15.1',
    gate: unitsGate,
    nullable: false,
    askedWhen: logicIncludes(UNITS_LOGIC),
    control: 'select-other',
    choices: [
      {
        label:
          'Same claim type (professional to professional, institutional to institutional)',
        value: 'same',
      },
      { label: 'Across claim types', value: 'across' },
    ],
  },
  {
    id: 'lookback-includes-same-day',
    num: '16',
    label: 'Lookback includes same day',
    group: 'Conditional decision set',
    start: 'ASK',
    required: 'Y*',
    kind: 'asked',
    ref: 'contract 16.0',
    gate: unitsGate,
    nullable: false,
    askedWhen: logicIncludes(UNITS_LOGIC),
    control: 'select-other',
    choices: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
  },
  {
    id: 'period-basis',
    num: '17',
    label: 'Period basis',
    group: 'Conditional decision set',
    start: 'DEFAULT',
    required: 'Y*',
    kind: 'asked',
    ref: 'contract 17.0 · defaults: rolling 365 days, rolling month = 30 days',
    gate: unitsGate,
    nullable: false,
    askedWhen: logicIncludes(UNITS_LOGIC),
    control: 'select-other',
    /* Nucleus's `period_basis` keys; its "varies by code group" is described
       under Something else. */
    choices: [
      { label: 'Rolling — days', value: 'rolling_days' },
      { label: 'Rolling — weeks', value: 'rolling_weeks' },
      { label: 'Rolling — months', value: 'rolling_months' },
      { label: 'Rolling — years', value: 'rolling_years' },
      { label: 'Fixed — days', value: 'fixed_days' },
      { label: 'Fixed — weeks', value: 'fixed_weeks' },
      { label: 'Fixed — months', value: 'fixed_months' },
      { label: 'Fixed — years', value: 'fixed_years' },
      { label: 'Something else — describe it', value: 'other' },
    ],
  },
  {
    id: 'provider-matching',
    num: '18',
    label: 'Provider matching',
    group: 'Conditional decision set',
    start: 'DEFAULT',
    required: 'Y*',
    kind: 'asked',
    ref: 'contract 18.0 · named schemes',
    gate: unitsGate,
    nullable: false,
    askedWhen: logicIncludes(UNITS_LOGIC),
    control: 'select-other',
    /* Nucleus's `provider_matching_scheme` keys. */
    choices: [
      {
        label: 'Comprehensive Matching (200Y|350Y|600Y|550Y|800Y)',
        value: 'comprehensive_matching',
      },
      {
        label: 'Rendering Provider NPI Only (200Y)',
        value: 'rendering_provider_npi_only',
      },
      {
        label: 'Provider NPI + Specialty (200Y|350Y)',
        value: 'provider_npi_specialty',
      },
      {
        label: 'Provider TIN Only (550Y|550N|700N|800Y|800N)',
        value: 'provider_tin_only',
      },
      {
        label: 'Different Providers in Same Group & Specialty (350Y|600Y|550Y|800Y)',
        value: 'different_providers_same_group_specialty',
      },
      {
        label: 'Billing Provider Taxonomy Code Only',
        value: 'billing_provider_taxonomy_only',
      },
      {
        label: 'Rendering Provider NPI + Provider TIN (200Y|550Y|550N|700N|800Y|800N)',
        value: 'rendering_npi_provider_tin',
      },
      {
        label: 'Specialty or Rendering Taxonomy Code Only',
        value: 'specialty_or_rendering_taxonomy_only',
      },
      {
        label: 'Humana: separate subtree — same TaxID + matching taxonomy/subspecialty',
        value: 'humana_subtree',
      },
      { label: 'Something else — describe it', value: 'other' },
    ],
  },
  {
    id: 'partial-pay-or-full-denial',
    num: '19',
    label: 'Partial pay or full denial',
    group: 'Conditional decision set',
    start: 'ASK',
    required: '—',
    kind: 'gated',
    ref: 'contract 19',
    note: 'Gated to units. This policy answered 14.0 as frequency, so it does not fire.',
  },
  {
    id: 'null-handling',
    num: '21',
    label: 'Null handling',
    group: 'Conditional decision set',
    start: 'ASK',
    required: 'As needed',
    kind: 'open',
    ref: 'contract 21.0 · do not enumerate until the shape is settled',
    gate: 'Attaches to a field, not to the policy',
    nullable: false,
    control: 'prose',
    prompt:
      'If a field this policy depends on is missing or blank on the claim, what should happen?',
  },
  {
    id: 'non-match-insight',
    num: '21.1',
    label: 'Insight type on non-match',
    group: 'Conditional decision set',
    start: 'ASK',
    required: '—',
    kind: 'gated',
    ref: 'contract 21.1',
    note: 'Requires a companion code.',
    control: 'select-other',
    /* The four values `PolicyInsight.insightType` actually accepts, so the
       answer is one design can paste in rather than translate. */
    choices: INSIGHT_TYPES.map((value) => ({ label: value, value })),
    askedWhen: (answers) =>
      choiceAnswer(answers['code-combination'])?.choice === 'companion',
  },
  {
    id: 'diagnosis-matching-level',
    num: '21.2',
    label: 'Diagnosis matching level',
    group: 'Conditional decision set',
    start: 'ASK',
    required: '—',
    kind: 'open',
    ref: 'contract 21.2',
    note: 'No diagnosis requirement here, so it does not fire. Values settled; cardinality reopened 2026-09-14 — one answer per policy, or one per claim type? CP-30981 states a different level for professional and outpatient on one policy.',
  },
  {
    id: 'source-kind',
    num: '22',
    label: 'Source · kind',
    group: 'Sources and evidence',
    start: 'ASK',
    required: '—',
    kind: 'inherited',
    ref: 'contract 22',
    note: 'Document. Set on the source record before scoping.',
  },
  {
    id: 'source-role',
    num: '23',
    label: 'Source · role',
    group: 'Sources and evidence',
    start: 'ASK',
    required: '—',
    kind: 'inherited',
    ref: 'contract 23',
    note: 'Primary. The source that initiated scoping.',
  },
  {
    id: 'source-name',
    num: '24',
    label: 'Source · name',
    group: 'Sources and evidence',
    start: 'DERIVED',
    required: '—',
    kind: 'inherited',
    ref: 'contract 24.0 · slots N=1–8',
    note: 'Taken from the primary source record, not asked.',
  },
  {
    id: 'source-urls',
    num: '25',
    label: 'Source · URL(s)',
    group: 'Sources and evidence',
    start: 'ASK',
    required: '—',
    kind: 'inherited',
    ref: 'contract 25',
    note: 'Inherited from the source library record.',
  },
  {
    id: 'source-type-level-1',
    num: '26',
    label: 'Source · type level 1',
    group: 'Sources and evidence',
    start: 'ASK',
    required: '—',
    kind: 'inherited',
    ref: 'contract 26',
    note: 'State Medicaid manual. Inherited from the source record.',
  },
  {
    id: 'source-type-level-2',
    num: '27',
    label: 'Source · type level 2',
    group: 'Sources and evidence',
    start: 'ASK',
    required: '—',
    kind: 'inherited',
    ref: 'contract 27',
    note: 'Provider manual appendix. Inherited from the source record.',
  },
  {
    id: 'source-excerpts',
    num: '28',
    label: 'Source · excerpt(s)',
    group: 'Sources and evidence',
    start: 'DERIVED',
    required: 'Y',
    kind: 'asked',
    ref: 'contract 28.0 · 1..N per source',
    nullable: false,
  },
  {
    id: 'source-curation-instructions',
    num: '28.1',
    label: 'Source · curation instructions',
    group: 'Sources and evidence',
    start: 'ASK',
    required: '—',
    kind: 'retired',
    ref: 'contract 28.1',
    note: 'Superseded 2026-09-13 — captured once at source intake, not re-entered per policy.',
  },
  {
    id: 'policy-effective-start-date',
    num: '29',
    label: 'Policy effective start date',
    group: 'Timeline',
    start: 'DERIVED',
    required: 'Y',
    kind: 'asked',
    ref: 'contract 29.0',
    nullable: false,
    control: 'date',
  },
  {
    id: 'policy-effective-end-date',
    num: '29.1',
    label: 'Policy effective end date',
    group: 'Timeline',
    start: 'ASK',
    required: '—',
    kind: 'notasked',
    ref: 'contract 29.1',
    note: 'Normally empty. No source states an end date, so this is never raised as a question.',
    control: 'date',
  },
  {
    id: 'policy-id',
    num: '30',
    label: 'policy_ID / unique concept identifier',
    group: 'System and PM-ops',
    start: 'ASK',
    required: '—',
    kind: 'system',
    ref: 'contract 30',
    note: 'System assigned.',
  },
  {
    id: 'target-date',
    num: '31',
    label: 'Target date',
    group: 'System and PM-ops',
    start: 'ASK',
    required: '—',
    kind: 'dropped',
    ref: 'contract 31',
    note: 'Dropped 2026-09-13 — build-planning date, stays in Jira. The client-committed half became 31.1 and 31.2.',
  },
  {
    id: 'committed-client',
    num: '31.1',
    label: 'Committed client',
    group: 'Commitment',
    start: 'ASK',
    required: '—',
    kind: 'open',
    ref: 'contract 31.1',
    note: 'Required only when a committed date is set.',
    control: 'chip-list',
  },
  {
    id: 'committed-date',
    num: '31.2',
    label: 'Committed date',
    group: 'Commitment',
    start: 'ASK',
    required: '—',
    kind: 'open',
    ref: 'contract 31.2',
    note: 'Committed dates are always a Friday.',
    control: 'date',
  },
  {
    id: 'labels',
    num: '32',
    label: 'Labels',
    group: 'System and PM-ops',
    start: 'ASK',
    required: '—',
    kind: 'dropped',
    ref: 'contract 32',
    note: 'Dropped 2026-09-13 — Jira initiative label propagation.',
  },
  {
    id: 'related-tickets',
    num: '33',
    label: 'Related tickets',
    group: 'System and PM-ops',
    start: 'ASK',
    required: '—',
    kind: 'dropped',
    ref: 'contract 33',
    note: 'Dropped 2026-09-13 — Jira issue links. May return as a policy-to-policy relation.',
  },
  {
    id: 'assignee-reporter',
    num: '34',
    label: 'Assignee / reporter',
    group: 'System and PM-ops',
    start: 'ASK',
    required: '—',
    kind: 'dropped',
    ref: 'contract 34',
    note: 'Dropped 2026-09-13 — becomes queue ownership, not a contract field.',
  },
]

export const contractField = (id: string): ContractField | undefined =>
  CONTRACT_FIELDS.find((field) => field.id === id)

/** Whether a row applies to this policy given the answers so far. */
export const isAsked = (
  field: ContractField,
  answers: Record<string, AnswerValue>,
): boolean => !field.askedWhen || field.askedWhen(answers)

/** Rows scoping can answer: asked, open rows that have a control to answer
 *  with, or gated rows whose condition the answers can meet. */
export const isWorksheetRow = (field: ContractField): boolean =>
  field.kind === 'asked' ||
  (field.kind === 'open' && Boolean(field.control)) ||
  (field.kind === 'gated' && Boolean(field.askedWhen))

/** A row belongs on the worksheet once scoping pulled it in explicitly, or
 *  it's a normal worksheet row that applies given the answers so far. */
export const onWorksheet = (
  field: ContractField,
  view: {
    answers: Record<string, AnswerValue>
    pulledIn: Record<string, unknown>
  },
): boolean =>
  field.id in view.pulledIn ||
  (isWorksheetRow(field) && isAsked(field, view.answers))

export interface SettledAnswer {
  text: string
  /** True when nothing was really answered, so design can see the difference. */
  muted: boolean
}

export const codeSummary = (field: ContractField, codes: string[]): string => {
  const lead = field.leadCount ?? 0
  return (field.summary ?? '{all} codes')
    .replace('{lead}', String(Math.min(lead, codes.length)))
    .replace('{rest}', String(Math.max(codes.length - lead, 0)))
    .replace('{all}', String(codes.length))
}

/**
 * The answer as design reads it.
 *
 * Never blank: an unanswered field says so in words, because "empty" and
 * "deliberately left empty" look identical otherwise and design cannot tell
 * whether to ask about it.
 */
export const settledAnswer = (
  field: ContractField,
  value: AnswerValue | undefined,
): SettledAnswer => {
  if (field.control === 'chips' || field.control === 'chip-list') {
    const list = asList(value)
    return list.length
      ? { text: list.join(', '), muted: false }
      : { text: 'None selected', muted: true }
  }
  if (field.control === 'codes') {
    const list = asList(value)
    if (!list.length) return { text: 'No codes listed', muted: true }
    return {
      text: `${list.join(', ')} — ${codeSummary(field, list)}`,
      muted: false,
    }
  }
  if (field.control === 'select-other') {
    const answer = choiceAnswer(value, field)
    if (!answer?.choice) return { text: '— not answered', muted: true }
    if (answer.choice === 'other')
      return answer.other?.trim()
        ? { text: answer.other.trim(), muted: false }
        : { text: 'Something else — not described', muted: true }
    const label = field.choices?.find((c) => c.value === answer.choice)?.label
    return { text: label ?? answer.choice, muted: false }
  }
  const text = typeof value === 'string' ? value.trim() : ''
  return text ? { text, muted: false } : { text: 'Left empty', muted: true }
}
